import { SaveOutlined, CheckOutlined, DownOutlined } from '@ant-design/icons';
import { Row, Col, Card, Statistic, Divider, Button, Space, InputNumber, Form, Input, Dropdown, Menu } from 'antd';
import React, { useState, useEffect } from 'react';

interface InvoiceSummaryProps {
  totalAmount: number;
  discountAmount: number;
  totalDiscountAmount: number; // Toplu indirim tutarı
  subtotalAmount: number;
  vatAmount: number;
  netAmount: number;
  onSubmit: () => void;
  loading: boolean;
  form: any;
  currencyCode?: string;
  openCashPaymentModal?: () => void;
  exchangeRate?: number; // TL karşılığı için kur değeri
  distributeDiscountAmount?: (amount: number, preserveExistingDiscounts?: boolean) => void; // Toplu indirim dağıtma fonksiyonu
}

const InvoiceSummary: React.FC<InvoiceSummaryProps> = ({
  totalAmount,
  discountAmount,
  totalDiscountAmount,
  subtotalAmount,
  vatAmount,
  netAmount,
  onSubmit,
  loading,
  form,
  currencyCode,
  openCashPaymentModal,
  exchangeRate = 1, // Varsayılan değer 1
  distributeDiscountAmount
}) => {
  // Para birimi state'i
  const [currency, setCurrency] = useState<string>(currencyCode || 'TRY');
  
  // Para birimi prop'u değiştiğinde state'i güncelle
  useEffect(() => {
    if (currencyCode && currencyCode !== currency) {
      setCurrency(currencyCode);
    }
  }, [currencyCode]);
  
  // Para birimi sembolünü belirle
  const getCurrencySymbol = (code: string) => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return code; // Bilinmeyen para birimleri için kodu göster
    }
  };
  
  const currencySymbol = getCurrencySymbol(currency);
  return (
    <div className="invoice-summary">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Fatura Notları" variant="borderless">
            <Form.Item
              name="notes"
              noStyle
            >
              <Input.TextArea 
                rows={4} 
                placeholder="Fatura ile ilgili notlar..."
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Card>
        </Col>
        
        <Col xs={24} md={12}>
          <Card title="Fatura Toplamları" variant="borderless">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic 
                  title="Toplam Tutar" 
                  value={totalAmount} 
                  precision={2} 
                  suffix={currencySymbol}
                  valueStyle={{ color: '#1890ff' }}
                />
                {currency !== 'TRY' && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    TL: {(totalAmount * exchangeRate).toFixed(2)} ₺
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Statistic 
                  title="İskonto Tutarı" 
                  value={discountAmount} 
                  precision={2} 
                  suffix={currencySymbol}
                  valueStyle={{ color: '#ff4d4f' }}
                />
                {currency !== 'TRY' && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    TL: {(discountAmount * exchangeRate).toFixed(2)} ₺
                  </div>
                )}
              </Col>
              <Col span={12}>
                <div>
                  <div style={{ fontSize: 14, color: '#8c8c8c', marginBottom: 4 }}>Tutar İndirim</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Form.Item
                      name="totalDiscountAmount"
                      noStyle
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        formatter={(value) => `${value} ${currencySymbol}`}
                        parser={(value: string | undefined): string | number => {
                          if (!value) return 0;
                          const parsed = parseFloat(value.replace(` ${currencySymbol}`, ''));
                          return isNaN(parsed) ? 0 : parsed;
                        }}
                        onChange={(value) => {
                          // Form alanını güncelle (ondalıklı değerleri koru)
                          form.setFieldsValue({ totalDiscountAmount: value });
                        }}
                      />
                    </Form.Item>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'distribute',
                            label: 'Satırlara Dağıt',
                            icon: <DownOutlined />,
                            onClick: () => {
                              // Form'dan toplu indirim tutarını al
                              const totalDiscountValue = form.getFieldValue('totalDiscountAmount');
                              if (totalDiscountValue && totalDiscountValue > 0 && distributeDiscountAmount) {
                                // Toplu indirim tutarını ürünlere dağıt (mevcut iskontoları sıfırlayarak)
                                distributeDiscountAmount(totalDiscountValue, false);
                              } else if (!distributeDiscountAmount) {
                                console.error('distributeDiscountAmount fonksiyonu tanımlanmamış');
                              }
                            }
                          },
                          {
                            key: 'distributePreserve',
                            label: 'Mevcut İskontoları Koruyarak Dağıt',
                            icon: <DownOutlined />,
                            onClick: () => {
                              // Form'dan toplu indirim tutarını al
                              const totalDiscountValue = form.getFieldValue('totalDiscountAmount');
                              if (totalDiscountValue && totalDiscountValue > 0 && distributeDiscountAmount) {
                                // Toplu indirim tutarını ürünlere dağıt (mevcut iskontoları koruyarak)
                                distributeDiscountAmount(totalDiscountValue, true);
                              } else if (!distributeDiscountAmount) {
                                console.error('distributeDiscountAmount fonksiyonu tanımlanmamış');
                              }
                            }
                          },
                          {
                            key: 'reset',
                            label: 'Tüm İskontoları Sıfırla',
                            danger: true,
                            onClick: () => {
                              // İndirim tutarını sıfırla
                              form.setFieldsValue({ totalDiscountAmount: 0 });
                              // Tüm satır iskontolarını sıfırla
                              if (distributeDiscountAmount) {
                                distributeDiscountAmount(0);
                              }
                            }
                          }
                        ]
                      }}
                      disabled={!distributeDiscountAmount}
                      placement="bottomRight"
                      trigger={['click']}
                    >
                      <Button 
                        type="primary" 
                        size="middle"
                        icon={<DownOutlined />}
                      />
                    </Dropdown>
                  </div>
                  {currency !== 'TRY' && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      TL: {(totalDiscountAmount * exchangeRate).toFixed(2)} ₺
                    </div>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <Statistic 
                  title="Ara Toplam" 
                  value={subtotalAmount} 
                  precision={2} 
                  suffix={currencySymbol}
                />
                {currency !== 'TRY' && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    TL: {(subtotalAmount * exchangeRate).toFixed(2)} ₺
                  </div>
                )}
              </Col>
              <Col span={12}>
                <Statistic 
                  title="KDV Tutarı" 
                  value={vatAmount} 
                  precision={2} 
                  suffix={currencySymbol}
                />
                {currency !== 'TRY' && (
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    TL: {(vatAmount * exchangeRate).toFixed(2)} ₺
                  </div>
                )}
              </Col>
              <Divider style={{ margin: '12px 0' }} />
              <Col span={24}>
                <Statistic 
                  title="Genel Toplam" 
                  value={netAmount} 
                  precision={2} 
                  suffix={currencySymbol}
                  valueStyle={{ color: '#52c41a', fontWeight: 'bold', fontSize: '1.2em' }}
                />
                {currency !== 'TRY' && (
                  <div style={{ fontSize: '14px', color: '#52c41a', fontWeight: 'bold' }}>
                    TL: {(netAmount * exchangeRate).toFixed(2)} ₺
                  </div>
                )}
              </Col>
            </Row>
          </Card>
        </Col>
        
        {/* Kaydet butonu kaldırıldı - InvoiceForm.tsx içindeki buton kullanılacak */}
      </Row>
    </div>
  );
};

export default InvoiceSummary;
